import { ALL_BOOKINGS_OPTION, EventReportBookingOptions } from '@contexts/event-report/event-report-context.type';
import { LinearGraphGroupBy } from '@domain/event-report';
import { EventSalesStatsGroupByResponse } from '@hooks/events/use-get-event-sales-stats-groupby';
import { EventViewsStatsResponse } from '@hooks/events/use-get-event-views-stats';
import { extractDateTimeParts } from '@utils/date-time-format/extract-date-time-parts';
import { TFunction } from 'i18next';
import moment from 'moment';

import {
  EVENT_REPORT_BOTTLE_SERVICE_TYPE,
  EVENT_REPORT_GUEST_LIST_TYPE,
  EVENT_REPORT_INVITATION_TYPE,
  EVENT_REPORT_PASS_TYPE,
  EVENT_REPORT_TAB_TIME_DAY,
  EVENT_REPORT_TAB_TIME_HOUR,
  EVENT_REPORT_TAB_TIME_WEEK,
  EVENT_REPORT_TAB_TYPE_BOOKING,
  EVENT_REPORT_TAB_TYPE_REVENUE,
  EVENT_REPORT_TAB_TYPE_VIEWS,
  EVENT_REPORT_TICKET_TYPE,
} from './config';
import { FormatLinearChartDataArguments } from './types';
import { createDefaultAdmissionBadges, createDefaultChannelsBadges, createFilteredAdmissionBadges } from '.';

const createTimeTabs = (t: TFunction<'translation', undefined>) => {
  return [
    {
      id: EVENT_REPORT_TAB_TIME_HOUR,
      name: t('eventReport:hourTabLabel'),
    },
    {
      id: EVENT_REPORT_TAB_TIME_DAY,
      name: t('eventReport:dayTabLabel'),
    },
    {
      id: EVENT_REPORT_TAB_TIME_WEEK,
      name: t('eventReport:weekTabLabel'),
    },
  ];
};

const createDataTypeTabs = (t: TFunction<'translation', undefined>) => {
  return [
    {
      id: EVENT_REPORT_TAB_TYPE_BOOKING,
      name: t('analytics:titleSoldItems'),
    },
    {
      id: EVENT_REPORT_TAB_TYPE_REVENUE,
      name: t('eventReport:revenueTabLabel'),
    },
    {
      id: EVENT_REPORT_TAB_TYPE_VIEWS,
      name: t('eventReport:viewTabLabel'),
    },
  ];
};

const getFirstDayOfWeek = (yearWeek: string) => {
  const [year, week] = yearWeek.split('-').map(Number);
  const date = moment().year(year).isoWeek(week).day(1);

  return date;
};

const createFirstAndLastDayOfData = (
  data: EventSalesStatsGroupByResponse | EventViewsStatsResponse,
  groupBy: LinearGraphGroupBy
) => {
  const isGroupedByWeek = groupBy === EVENT_REPORT_TAB_TIME_WEEK;
  const firstYearWeek = Object.keys(data)[0];
  const lastYearWeek = Object.keys(data)[Object.keys(data).length - 1];

  const firstDataDay = isGroupedByWeek ? getFirstDayOfWeek(firstYearWeek) : moment(firstYearWeek);
  const lastDataDay = isGroupedByWeek ? getFirstDayOfWeek(lastYearWeek) : moment(lastYearWeek);
  return { firstDataDay, lastDataDay };
};

export const formatKeyLinearChart = (
  key: string,
  firstDay: moment.Moment,
  lastDay: moment.Moment,
  groupBy: LinearGraphGroupBy,
  locale: string
) => {
  switch (groupBy) {
    case EVENT_REPORT_TAB_TIME_HOUR: {
      const date = moment(key).unix();
      const parts = extractDateTimeParts(date, locale);
      const diffInDays = lastDay.diff(firstDay, 'days');
      return diffInDays <= 1
        ? `${parts.hour24}:${parts.minute}`
        : `${parts.weekday} ${parts.day} ${parts.hour24}:${parts.minute}`;
    }

    case EVENT_REPORT_TAB_TIME_DAY: {
      const date = moment(key).unix();
      const parts = extractDateTimeParts(date, locale);
      return `${parts.weekday} ${parts.day}`;
    }

    case EVENT_REPORT_TAB_TIME_WEEK: {
      const startOfWeek = getFirstDayOfWeek(key).startOf('isoWeek');
      const endOfWeek = getFirstDayOfWeek(key).endOf('isoWeek');

      const startParts = extractDateTimeParts(startOfWeek.unix(), locale);
      const endParts = extractDateTimeParts(endOfWeek.unix(), locale);

      const startMonthFormatted = startParts.month === endParts.month ? '' : ` ${startParts.month}`;

      return `${startParts.day}${startMonthFormatted}-${endParts.day} ${endParts.month}`;
    }

    default: {
      const date = moment(key).unix();
      const parts = extractDateTimeParts(date, locale);
      return `${parts.weekday} ${parts.day} ${parts.month}`;
    }
  }
};

export const createAdmissionsLinearData = (
  groupBy: LinearGraphGroupBy,
  data: EventSalesStatsGroupByResponse,
  bookingFilterValue: EventReportBookingOptions,
  timezone: string,
  locale: string
) => {
  if (Object.keys(data).length === 0) return [];

  const { firstDataDay, lastDataDay } = createFirstAndLastDayOfData(data, groupBy);
  const chartData = Object.keys(data).map((key) => {
    const dataOffers = data[key].offers;
    return {
      date: formatKeyLinearChart(key, firstDataDay, lastDataDay, groupBy, locale),
      [EVENT_REPORT_TICKET_TYPE]: dataOffers.ticket.soldItems[bookingFilterValue],
      [`${EVENT_REPORT_TICKET_TYPE} ${EVENT_REPORT_TAB_TYPE_REVENUE}`]: dataOffers.ticket.revenue[bookingFilterValue],
      [EVENT_REPORT_BOTTLE_SERVICE_TYPE]: dataOffers.bottleservice.soldItems[bookingFilterValue],
      [`${EVENT_REPORT_BOTTLE_SERVICE_TYPE} ${EVENT_REPORT_TAB_TYPE_REVENUE}`]:
        dataOffers.bottleservice.revenue[bookingFilterValue],
      [EVENT_REPORT_GUEST_LIST_TYPE]: dataOffers.guestlist.soldItems[bookingFilterValue],
      [`${EVENT_REPORT_GUEST_LIST_TYPE} ${EVENT_REPORT_TAB_TYPE_REVENUE}`]:
        dataOffers.guestlist.revenue[bookingFilterValue],
      [EVENT_REPORT_PASS_TYPE]: dataOffers.season_pass.soldItems[bookingFilterValue],
      [`${EVENT_REPORT_PASS_TYPE} ${EVENT_REPORT_TAB_TYPE_REVENUE}`]:
        dataOffers.season_pass.revenue[bookingFilterValue],
      [EVENT_REPORT_INVITATION_TYPE]: dataOffers.invitation.soldItems[bookingFilterValue],
      [`${EVENT_REPORT_INVITATION_TYPE} ${EVENT_REPORT_TAB_TYPE_REVENUE}`]:
        dataOffers.invitation.revenue[bookingFilterValue],
    };
  });

  return chartData;
};

export const createChannelsLinearData = (
  groupBy: LinearGraphGroupBy,
  data: EventViewsStatsResponse,
  selectedChannels: { id: string; name: string }[],
  timezone: string,
  locale: string
) => {
  if (Object.keys(data).length === 0) return [];

  const { firstDataDay, lastDataDay } = createFirstAndLastDayOfData(data, groupBy);
  const chartData = Object.keys(data).map((key) => {
    const channels = data[key].channels;
    const total = data[key].total;

    if (!channels)
      return {
        date: formatKeyLinearChart(key, firstDataDay, lastDataDay, groupBy, locale),
        total,
      };

    const channelData = Object.keys(channels).reduce((acc: { [key: string]: number }, channelId) => {
      const channelValue = channels[channelId];
      const foundChannel = selectedChannels.find((channel) => channel.id === channelId);
      if (foundChannel) acc[foundChannel.name.trim()] = channelValue;

      return acc;
    }, {});

    return {
      date: formatKeyLinearChart(key, firstDataDay, lastDataDay, groupBy, locale),
      ...channelData,
      total,
    };
  });
  return chartData;
};

export const formatDataLinearChartSection = ({
  data,
  eventSales,
  groupBy,
  t,
  bookingFilterValue = ALL_BOOKINGS_OPTION,
  dataType = EVENT_REPORT_TAB_TYPE_REVENUE,
  selectedChannels = [],
  timezone,
  locale,
}: FormatLinearChartDataArguments & { timezone: string; locale: string }) => {
  const isViewsTabActive = dataType === EVENT_REPORT_TAB_TYPE_VIEWS;
  const defaultInfo = {
    defaultDataTypeTab: dataType,
    dataTypeTabs: createDataTypeTabs(t),
    defaultTimeTab: EVENT_REPORT_TAB_TIME_DAY,
    timeTabs: createTimeTabs(t),
  };

  if (!data || !groupBy)
    return {
      data: [],
      event: undefined,
      categories: isViewsTabActive ? createDefaultChannelsBadges(t, selectedChannels) : createDefaultAdmissionBadges(t),
      ...defaultInfo,
    };

  const formattedData = isViewsTabActive
    ? createChannelsLinearData(groupBy, data as EventViewsStatsResponse, selectedChannels, timezone, locale)
    : createAdmissionsLinearData(groupBy, data as EventSalesStatsGroupByResponse, bookingFilterValue, timezone, locale);

  const categories = isViewsTabActive
    ? createDefaultChannelsBadges(t, selectedChannels)
    : createFilteredAdmissionBadges(t, eventSales, bookingFilterValue);

  return {
    data: formattedData,
    event: eventSales.event,
    categories,
    ...defaultInfo,
  };
};
